import { useCallback, useState } from "react";
import { defaultCities } from "../../app/common/config";
import { getWindow } from "../../helpers/getWindow";
import { useCountry } from "../../hooks/useCountry";
import { useIsPersistingForbidden } from "../../hooks/useIsPersistingForbidden";
import { sendError } from "../ErrorBoundary/helpers";
import config from "./config";
import type { FavoriteType, LocalStorageFavoriteItem } from "./types";

export const useFavorites = (): {
    favorites: Array<LocalStorageFavoriteItem>;
    removeFavorite: (cityCode: string) => void;
} => {
    const window = getWindow();
    const country = useCountry();
    const isPersistingForbidden = useIsPersistingForbidden();
    const defaultCity = defaultCities[country];
    const localStorageKey = config.localStorageKey[country];
    const [favorites, setFavorites] = useState<Array<LocalStorageFavoriteItem>>(
        () => {
            if (isPersistingForbidden && defaultCity !== null) {
                return [defaultCity];
            }

            try {
                const favoriteStore =
                    window?.localStorage.getItem(localStorageKey);
                const favoriteStoreObj = JSON.parse(
                    favoriteStore ?? '{"favorites": []}'
                ) as { favorites: Array<FavoriteType> };

                if (favoriteStoreObj.favorites.length > 0) {
                    return favoriteStoreObj.favorites
                        .filter(
                            (item: FavoriteType) =>
                                Object.keys(item.forceOverwrite ?? [])
                                    .length === 0
                        )
                        .map((item: FavoriteType) => ({
                            code: item.cityCode,
                            name: item.cityName,
                            slug: item.seoString,
                            countryCode: item.countryCode,
                        }));
                } else if (defaultCity !== null) {
                    return [defaultCity];
                }
            } catch (e) {
                sendError(e);
            }

            return [];
        }
    );

    const removeFavorite = useCallback(
        (cityCode: string): void => {
            if (cityCode.length === 0 || isPersistingForbidden) {
                return;
            }

            try {
                const favoriteStore =
                    window?.localStorage.getItem(localStorageKey);
                const favoriteStoreObj = JSON.parse(
                    favoriteStore ?? '{"favorites": []}'
                ) as { favorites: Array<FavoriteType> };

                favoriteStoreObj.favorites = favoriteStoreObj.favorites.filter(
                    (item: FavoriteType) => item.cityCode !== cityCode
                );

                window?.localStorage.setItem(
                    localStorageKey,
                    JSON.stringify(favoriteStoreObj)
                );

                setFavorites(
                    favoriteStoreObj.favorites
                        .filter(
                            (item: FavoriteType) =>
                                Object.keys(item.forceOverwrite ?? [])
                                    .length === 0
                        )
                        .map((item: FavoriteType) => ({
                            code: item.cityCode,
                            name: item.cityName,
                            slug: item.seoString,
                            countryCode: item.countryCode,
                        }))
                );
            } catch (e) {
                sendError(e);
            }
        },
        [isPersistingForbidden, localStorageKey, window?.localStorage]
    );

    return { favorites, removeFavorite };
};
